import type { ComponentType } from 'react';
import { useParams } from '@tanstack/react-router';
import { I } from '../../shared/icons/icons.tsx';

/** Préfixe applicatif pro — voir docs/architecture */
export const PRO_BASE = '/pro';

export const PRACTICE_UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export function isPracticeUuid(value: string): boolean {
  return PRACTICE_UUID_RE.test(value);
}

export function practiceBase(practiceUuid: string): string {
  return `${PRO_BASE}/${practiceUuid}`;
}

export const paths = {
  dashboard: (practiceUuid: string) => practiceBase(practiceUuid),
  agenda: (practiceUuid: string) => `${practiceBase(practiceUuid)}/agenda`,
  today: (practiceUuid: string) => `${practiceBase(practiceUuid)}/today`,
  messages: (practiceUuid: string, conversationId?: number) =>
    conversationId != null
      ? `${practiceBase(practiceUuid)}/messages?conversation=${conversationId}`
      : `${practiceBase(practiceUuid)}/messages`,
  patients: (practiceUuid: string) => `${practiceBase(practiceUuid)}/patients`,
  firstContact: (practiceUuid: string) =>
    `${practiceBase(practiceUuid)}/first-contact`,
  firstAppointment: (practiceUuid: string) =>
    `${practiceBase(practiceUuid)}/first-appointment`,
  patientModule: (
    practiceUuid: string,
    patientUuid: string,
    moduleId: string,
  ) => `${practiceBase(practiceUuid)}/patients/${patientUuid}/${moduleId}`,
  userModule: (practiceUuid: string, userModuleId: string) =>
    `${practiceBase(practiceUuid)}/m/${userModuleId}`,
} as const;

export type PatientModuleId =
  | 'clinical'
  | 'civil'
  | 'billing'
  | 'history'
  | 'media'
  | 'exercises';

export interface NavItem {
  id: string;
  path: string;
  label: string;
  icon: ComponentType<{ size?: number }>;
  badge: number | null;
  badgeColor?: 'primary';
  keywords?: string[];
}

export function getNavItems(practiceUuid: string): NavItem[] {
  return [
    {
      id: 'dashboard',
      path: paths.dashboard(practiceUuid),
      label: 'Tableau de bord',
      icon: I.LayoutGrid,
      badge: null,
      keywords: ['accueil', 'dashboard', 'home', 'résumé', 'vue d’ensemble'],
    },
    {
      id: 'agenda',
      path: paths.agenda(practiceUuid),
      label: 'Agenda',
      icon: I.Calendar,
      badge: null,
      keywords: [
        'planning',
        'calendrier',
        'rendez-vous',
        'rdv',
        'calendar',
        'schedule',
      ],
    },
    {
      id: 'today',
      path: paths.today(practiceUuid),
      label: 'Patients du jour',
      icon: I.CalendarDay,
      badge: 12,
      keywords: [
        'aujourd’hui',
        'jour',
        'today',
        'liste du jour',
        'planning du jour',
      ],
    },
    {
      id: 'messages',
      path: paths.messages(practiceUuid),
      label: 'Messages patient',
      icon: I.Message,
      badge: 4,
      badgeColor: 'primary',
      keywords: [
        'chat',
        'messagerie',
        'conversation',
        'sms',
        'discussion',
        'inbox',
        'boîte de réception',
      ],
    },
    {
      id: 'patients',
      path: paths.patients(practiceUuid),
      label: 'Patients',
      icon: I.Users,
      badge: null,
      keywords: [
        'dossiers',
        'fichiers',
        'liste patients',
        'contacts',
        'people',
      ],
    },
    {
      id: 'first-contact',
      path: paths.firstContact(practiceUuid),
      label: 'Premier contact',
      icon: I.Phone,
      badge: 2,
      keywords: [
        'appel',
        'téléphone',
        'prospect',
        'nouveau',
        'lead',
        'demande',
      ],
    },
    {
      id: 'first-appointment',
      path: paths.firstAppointment(practiceUuid),
      label: 'Premier rendez-vous',
      icon: I.UserPlus,
      badge: null,
      keywords: [
        'premier rdv',
        'nouveau patient',
        'consultation initiale',
        'bilan',
      ],
    },
  ];
}

export interface PatientModuleNav {
  id: PatientModuleId;
  label: string;
  icon: string;
  keywords?: string[];
}

export const PATIENT_MODULE_NAV: PatientModuleNav[] = [
  {
    id: 'clinical',
    label: 'Dossier clinique',
    icon: 'ClipboardList',
    keywords: [
      'clinique',
      'soin',
      'traitement',
      'diagnostic',
      'notes',
      'dossier médical',
    ],
  },
  {
    id: 'civil',
    label: 'État civil',
    icon: 'UserPlus',
    keywords: [
      'identité',
      'coordonnées',
      'adresse',
      'naissance',
      'fiche civile',
      'informations',
    ],
  },
  {
    id: 'billing',
    label: 'Plan de gestion',
    icon: 'CreditCard',
    keywords: [
      'facturation',
      'paiement',
      'compta',
      'argent',
      'devis',
      'honoraires',
      'solde',
    ],
  },
  {
    id: 'history',
    label: 'Historique',
    icon: 'Clock',
    keywords: [
      'timeline',
      'évolution',
      'passé',
      'antécédents',
      'log',
      'journal',
    ],
  },
  {
    id: 'media',
    label: 'Médias',
    icon: 'Image',
    keywords: [
      'photos',
      'images',
      'radios',
      'scans',
      'documents',
      'fichiers',
      'pièces jointes',
    ],
  },
  {
    id: 'exercises',
    label: 'Suivi exercices',
    icon: 'Activity',
    keywords: [
      'exercices',
      'kinésithérapie',
      'mouvements',
      'rééducation',
      'protocole',
    ],
  },
];

const PATIENT_ROUTE_RE =
  /^\/pro\/[0-9a-f-]{36}\/patients\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})\/([a-z]+)$/i;

export function parsePatientRoute(
  pathname: string,
): { patientUuid: string; moduleId: PatientModuleId } | null {
  const m = PATIENT_ROUTE_RE.exec(pathname);
  if (!m) return null;
  const moduleId = m[2] as PatientModuleId;
  if (!PATIENT_MODULE_NAV.some((mod) => mod.id === moduleId)) return null;
  return { patientUuid: m[1]!, moduleId };
}

export function parsePracticeUuidFromPath(pathname: string): string | null {
  const segment = pathname.split('/')[2];
  return segment && isPracticeUuid(segment) ? segment : null;
}

export function isPatientRoute(pathname: string): boolean {
  return PATIENT_ROUTE_RE.test(pathname);
}

export function isDashboardRoute(pathname: string): boolean {
  const uuid = parsePracticeUuidFromPath(pathname);
  if (!uuid) return false;
  const rest = pathname.slice(practiceBase(uuid).length);
  return rest === '' || rest === '/';
}

const USER_MODULE_ROUTE_RE = /^\/pro\/[^/]+\/m\/([^/?]+)/;

export function parseUserModuleRoute(pathname: string): string | null {
  const m = USER_MODULE_ROUTE_RE.exec(pathname);
  return m ? (m[1] ?? null) : null;
}

export function isUserModuleRoute(pathname: string): boolean {
  return USER_MODULE_ROUTE_RE.test(pathname);
}

export function activeNavId(pathname: string, practiceUuid: string): string {
  if (isDashboardRoute(pathname)) return 'dashboard';
  const patient = parsePatientRoute(pathname);
  if (patient) return `patient-${patient.moduleId}`;
  const item = getNavItems(practiceUuid).find((n) => n.path === pathname);
  return item?.id ?? 'dashboard';
}

export function navItemById(
  id: string,
  practiceUuid: string,
): NavItem | undefined {
  return getNavItems(practiceUuid).find((n) => n.id === id);
}

export function patientModulePath(
  practiceUuid: string,
  patientUuid: string,
  moduleId: PatientModuleId,
): string {
  return paths.patientModule(practiceUuid, patientUuid, moduleId);
}

/** Remplace le cabinet dans l’URL courante (favoris par cabinet). */
export function replacePracticeInPath(
  pathname: string,
  practiceUuid: string,
): string {
  const parts = pathname.split('/').filter(Boolean);
  if (parts[0] === 'pro' && parts[1]) {
    parts[1] = practiceUuid;
    return `/${parts.join('/')}`;
  }
  return paths.dashboard(practiceUuid);
}

export function usePracticeUuid(): string {
  const { practiceUuid } = useParams({ strict: false });
  if (!practiceUuid || !isPracticeUuid(practiceUuid)) {
    throw new Error('Route sans paramètre practiceUuid');
  }
  return practiceUuid;
}
